import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

export default function SideNav({ items = [], topOffset = 110 }) {
  const [activeId, setActiveId] = useState('');   // vacío = ninguna sección activa aún
  const [onDark, setOnDark]     = useState(true);
  const [visible, setVisible]   = useState(true);
  const [showBack, setShowBack] = useState(false); // mostrar flecha "volver arriba"
  const rafRef = useRef(null);

  useEffect(() => {
    const compute = () => {
      const scrollY = window.scrollY;

      // ── Mostrar flecha de regreso al top cuando el usuario bajó ──
      setShowBack(scrollY > 200);

      // ── Sección activa: solo si el usuario bajó suficiente ─────────
      let current = '';
      if (scrollY > 80) {
        items.forEach(({ id }) => {
          const el = document.getElementById(id);
          if (el && el.getBoundingClientRect().top <= 140) current = id;
        });
      }
      setActiveId(current);

      // ── Detectar luminancia SOLO cuando el usuario ya salió del hero ──
      // Mientras estamos en el hero (foto de fondo) siempre es dark
      const heroEl = document.getElementById('page-hero');
      const heroBottom = heroEl
        ? heroEl.getBoundingClientRect().bottom
        : window.innerHeight;

      if (heroBottom > 80) {
        // Aún dentro del hero → siempre dark
        setOnDark(true);
        return;
      }

      // Fuera del hero → detectar por elementFromPoint
      const sideX = window.innerWidth - 60;
      const sideY = topOffset + 80;
      const el = document.elementFromPoint(sideX, sideY);
      if (el) {
        let node = el;
        let bg = '';
        while (node && node !== document.body) {
          const c = window.getComputedStyle(node).backgroundColor;
          if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') { bg = c; break; }
          node = node.parentElement;
        }
        if (bg) {
          const m = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (m) {
            const lum = 0.2126 * +m[1] + 0.7152 * +m[2] + 0.0722 * +m[3];
            setOnDark(lum < 160);
          }
        }
      }
    };

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(compute);
    };

    compute(); // inicial
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [items, topOffset]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
  };

  // ── Paleta adaptativa con transición CSS ──────────────────────────
  const colorBase   = onDark ? '#ffffff' : 'rgba(26,26,26,0.55)';
  const colorActive = onDark ? '#ffffff'                : '#1A1A1A';
  const lineBase    = onDark ? 'rgba(255,255,255,0.80)' : 'rgba(212,175,55,0.35)';
  const lineActive  = onDark ? '#ffffff'                : '#D4AF37';
  const shadow      = onDark ? '0 1px 3px rgba(0,0,0,0.9), 0 2px 12px rgba(0,0,0,0.7)' : 'none';
  const arrowColor  = onDark ? '#ffffff' : 'rgba(26,26,26,0.55)';
  const arrowHover  = onDark ? '#ffffff'                    : '#1A1A1A';

  return (
    <Box sx={{
      position: 'fixed',
      top: topOffset,
      right: { xs: 14, md: 28 },
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 0.3,
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? 'all' : 'none',
      transition: 'opacity 0.4s ease',
    }}>

      {/* ── Flecha suave "volver al inicio" ── */}
      <Box
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.8,
          cursor: 'pointer',
          py: 0.4,
          px: 0.5,
          mb: 1,
          opacity: showBack ? 1 : 0,
          transform: showBack ? 'translateY(0)' : 'translateY(-6px)',
          pointerEvents: showBack ? 'all' : 'none',
          transition: 'all 0.35s ease',
          '&:hover .sn-back-label': { color: `${arrowHover} !important` },
          '&:hover .sn-back-icon':  { color: `${arrowHover} !important`, transform: 'translateY(-2px)' },
        }}
      >
        <Typography className="sn-back-label" sx={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 500,
          fontSize: '0.58rem',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: arrowColor,
          lineHeight: 1,
          userSelect: 'none',
          whiteSpace: 'nowrap',
          transition: 'color 0.3s ease',
          textShadow: shadow,
        }}>
          Inicio
        </Typography>
        <KeyboardArrowUpIcon className="sn-back-icon" sx={{
          fontSize: 14,
          color: arrowColor,
          transition: 'all 0.3s ease',
        }} />
      </Box>

      {/* ── Ítems de navegación ── */}
      {items.map((item) => {
        const isActive = activeId === item.id;
        return (
          <Box
            key={item.id}
            onClick={() => scrollTo(item.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              cursor: 'pointer',
              py: 0.4,
              px: 0.5,
              borderRadius: 1,
              '&:hover .sn-label': { color: `${colorActive} !important` },
              '&:hover .sn-line':  { width: '22px !important', background: `${lineActive} !important` },
            }}
          >
            <Typography className="sn-label" sx={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: isActive ? 700 : 400,
              fontSize: '0.62rem',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: isActive ? colorActive : colorBase,
              lineHeight: 1,
              userSelect: 'none',
              whiteSpace: 'nowrap',
              transition: 'color 0.35s ease, font-weight 0.25s ease',
              textShadow: shadow,
            }}>
              {item.label}
            </Typography>

            <Box className="sn-line" sx={{
              height: '1.5px',
              width: isActive ? 22 : 12,
              background: isActive ? lineActive : lineBase,
              borderRadius: 1,
              flexShrink: 0,
              transition: 'all 0.35s ease',
            }} />
          </Box>
        );
      })}
    </Box>
  );
}
